import React, {useEffect, useState} from 'react';
import PropTypes from "prop-types";
import clsx from "clsx";
import {makeStyles, useTheme, Typography} from "@material-ui/core";
import CarAvatar from "./CarAvatar";
import ArchivedAvatar from "./ArchivedAvatar";
import {themeTypeToBool} from '../../../utils/helpers';
import SpeedIcon from '@material-ui/icons/Speed';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import CompassCalibrationIcon from '@material-ui/icons/CompassCalibration';
import DriveEtaIcon from '@material-ui/icons/DriveEta';
import DriveDescriptionIcon from '@material-ui/icons/Description';
import RoomIcon from '@material-ui/icons/Room';
import Battery from '@material-ui/icons/Battery90';
import t from "../../../languages/pl";
import CarStatus from './CarStatus';
import {TooltipTop as CustomTooltip, TooltipRight} from "../../Common/CustomTooltips";
import {electricCarFuelTypeId} from "../../../config/constants";

const useStyles = makeStyles((theme) => ({
    container: {
        background: themeTypeToBool(theme.palette.type) ? "#FFF" : "#34344A",
        marginTop: theme.spacing(1),
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    content: {
        display: "flex",
        position: "relative",
        cursor: "pointer",
    },
    carDetails: {
        marginLeft: theme.spacing(1.5),
        marginBottom: theme.spacing(1),
        color: themeTypeToBool(theme.palette.type) ? "#43425D" : "#E5E5E5"
    },
    carAvatar: {
        marginTop: theme.spacing(1),
        marginLeft: theme.spacing(1)
    },
    carTitle: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        marginLeft: theme.spacing(1),
        paddingTop: theme.spacing(1),
        display: "flex",
        alignItems: "center",
        color: themeTypeToBool(theme.palette.type) ? "#454F63" : "#FFD1A4",
        borderBottom: '1px solid',
        borderBottomColor: themeTypeToBool(theme.palette.type) ? "#454F63" : "#FAFAFA",
    },
    singleInfo: {
        display: "flex",
        alignItems: "center",
    },
    adornmentIcon: {
        height: 15,
        width: 15,
    },
    addressReveal: {
        cursor: "pointer",
        textDecoration: "underline",
    },
    textClickable: {
        cursor: "pointer",
        "&:hover": {
            fontSize: '1.3rem',
            fontWeight: 700
        }
    },
    carSelected: {
        color: themeTypeToBool(theme.palette.type) ? "#121212" : "#E5E5E5"
    },
    carSelectedDiv: {
        color: "#43425D"
    },
    fuelReserve: {
        color: "orange"
    }
}));

function SingleCar(props) {
    const theme = useTheme();
    const {car} = props;
    const {
        id, name = "", registrationNo, fuelLevel = 0, fuelType, fuelTypeId, fuelPercent, fuelReserve,
        distance = 0, speed, address, lastGpsUpdate, status, type,
        selected = false, onAddressReveal, currentPosition, archived, driver = "", onRegistrationClick,
    } = props;
    const classes = useStyles(theme);

    const [fuelLevelLabel, setFuelLevelLabel] = useState('');

    useEffect(() => {
        const levelLabel = fuelTypeId === electricCarFuelTypeId ?
            `${fuelLevel}% / ${fuelType}` :
            `${fuelLevel} l (${Math.round(fuelPercent * 100)}%) / ${fuelType}`;
        setFuelLevelLabel(levelLabel);
    }, [fuelTypeId]);

    const handleAddressReveal = (e) => {
        e.stopPropagation();
        if (onAddressReveal)
            onAddressReveal(id);
    };

    const handleCarRegistrationClick = (e) => {
        e.stopPropagation();
        onRegistrationClick && onRegistrationClick(registrationNo);
    };

    const getAddress = () => {
        return (
            <span
                className={classes.addressReveal}
                style={{
                    color:
                        selected ?
                            (themeTypeToBool(theme.palette.type) ? "inherit" : "blue")
                            :
                            (themeTypeToBool(theme.palette.type) ? "blue" : "#9f9fea")
                }}
                onClick={e => handleAddressReveal(e)}
            >
                {t.addressReveal}
            </span>
        );
    };

    const showAddress = (location) => {
        if (location !== null && location !== "b.d.")
            return location;
        return getAddress();
    };

    const setBackground = (selected, error) => {
        if (selected)
            return themeTypeToBool(theme.palette.type) ? "#C6C6E8" : "#8180cc";
        if (error)
            return "#CEBEBE";
        return "inherit";
    };

    const getBatteryColor = (batteryLevel) => {
        if (+batteryLevel <= 11.5 && +batteryLevel >= 10.5) {
            return 'orange';
        } else if (+batteryLevel < 10.5) {
            return "#FF5353";
        }
        return '';
    };


    return (
        <React.Fragment>
            <TooltipRight
                title={t.unknownLocation}
                disable={currentPosition ? true : false}
            >
                <div
                    className={classes.container}
                    id={id}
                >
                    <div
                        style={{background: setBackground(selected, !currentPosition)}}
                        onClick={props.onClick}
                    >
                        <div className={classes.carTitle}>
                            {archived ? <ArchivedAvatar/> : <CarStatus status={status}/>}
                            <TooltipRight title={t.showCarDetails}>
                                <Typography
                                    variant="h6"
                                    className={clsx([classes.singleInfo, classes.textClickable])}
                                    onClick={handleCarRegistrationClick}
                                >
                                    {registrationNo}
                                    <Typography
                                        variant="body2"
                                        className={classes.carDetails}
                                        style={{marginBottom: 0, fontSize: "0.8rem"}}
                                        color="secondary">
                                        {driver}
                                    </Typography>
                                </Typography>
                            </TooltipRight>
                        </div>
                        <div className={classes.content}>
                            <div className={classes.carAvatar}>
                                <CarAvatar
                                    selected={selected}
                                    type={type}
                                />
                            </div>
                            <div>
                                <div className={clsx(classes.carDetails, {[classes.carSelectedDiv]: selected})}>
                                    <Typography
                                        variant="body2"
                                        className={clsx(classes.singleInfo, {[classes.carSelected]: selected})}
                                    >
                                        <CustomTooltip title={t.car}>
                                            <DriveEtaIcon
                                                className={clsx(classes.adornmentIcon, {[classes.carSelected]: selected})}/>
                                        </CustomTooltip>
                                        &nbsp;
                                        {name}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        className={clsx(classes.singleInfo, {[classes.carSelected]: selected})}
                                    >
                                        <CustomTooltip title={t.description}>
                                            <DriveDescriptionIcon
                                                className={clsx(classes.adornmentIcon, {[classes.carSelected]: selected})}/>
                                        </CustomTooltip>
                                        &nbsp;
                                        {car.opis}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        className={clsx(classes.singleInfo, {[classes.carSelected]: selected})}
                                    >
                                        <CustomTooltip title={t.meterReading + "/" + t.averageSpeed}>
                                            <SpeedIcon
                                                className={clsx(classes.adornmentIcon, {[classes.carSelected]: selected})}/>
                                        </CustomTooltip>
                                        &nbsp;
                                        {distance} km / {speed} km/h
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        className={clsx(classes.singleInfo, {
                                            [classes.fuelReserve]: fuelReserve,
                                            [classes.carSelected]: selected
                                        })}
                                    >
                                        <CustomTooltip title={t.fuelLevel}>
                                            <LocalGasStationIcon className={clsx(classes.adornmentIcon, {
                                                [classes.fuelReserve]: fuelReserve,
                                                [classes.carSelected]: selected
                                            })}/>
                                        </CustomTooltip>
                                        &nbsp;
                                        {fuelLevelLabel}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        style={{color: getBatteryColor(car.stan_akumulatora)}}
                                        className={clsx(classes.singleInfo, {
                                            [classes.carSelected]: selected
                                        })}
                                    >
                                        <CustomTooltip title={t.batteryCondition}>
                                            <Battery className={clsx(classes.adornmentIcon, {
                                                [classes.carSelected]: selected
                                            })}
                                            style={{paddingBottom: '2px'}}/>
                                        </CustomTooltip>
                                        &nbsp;
                                        <span style={{paddingTop: '2px'}}>{car.stan_akumulatora}V</span>
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        className={clsx(classes.singleInfo, {[classes.carSelected]: selected})}
                                    >
                                        <CustomTooltip title={t.location}>
                                            <RoomIcon
                                                className={clsx(classes.adornmentIcon, {[classes.carSelected]: selected})}/>
                                        </CustomTooltip>
                                        &nbsp;
                                        {showAddress(address)}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        className={clsx(classes.singleInfo, {[classes.carSelected]: selected})}
                                    >
                                        <CustomTooltip title={t.poi}>
                                            <RoomIcon
                                                className={clsx(classes.adornmentIcon, {[classes.carSelected]: selected})}/>
                                        </CustomTooltip>
                                        &nbsp;
                                        {t.poi}:{car.POI}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        className={clsx(classes.singleInfo, {[classes.carSelected]: selected})}
                                    >
                                        <CustomTooltip title={t.lastCommTimestamp}>
                                            <CompassCalibrationIcon
                                                className={clsx(classes.adornmentIcon, {[classes.carSelected]: selected})}/>
                                        </CustomTooltip>
                                        &nbsp;
                                        {lastGpsUpdate}
                                    </Typography>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </TooltipRight>
        </React.Fragment>
    );
}

export default SingleCar;

SingleCar.propTypes = {
    id: PropTypes.any,
    car: PropTypes.object,
    type: PropTypes.string,
    name: PropTypes.string,
    driver: PropTypes.string,
    registrationNo: PropTypes.string,
    fuelLevel: PropTypes.number,
    fuelPercent: PropTypes.number,
    fuelReserve: PropTypes.bool,
    fuelType: PropTypes.string,
    fuelTypeId: PropTypes.number,
    distance: PropTypes.number,
    speed: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    address: PropTypes.string,
    lastGpsUpdate: PropTypes.string,
    status: PropTypes.string,
    selected: PropTypes.bool,
    onClick: PropTypes.func,
    onAddressReveal: PropTypes.func,
    currentPosition: PropTypes.object,
    archived: PropTypes.bool,
    onRegistrationClick: PropTypes.func
};
